import clsx from 'clsx';
import { mapLabel } from 'components/CruiseSearchWidget/utils/reducers';
import { SelectedOption } from 'components/FiltersProvider/types';
import { CaretIcon } from 'components/Icon';
import { useAppContext } from 'contexts/appContext';
import { SVGProps, useMemo } from 'react';
import styles from './FilterDropdown.css';

export enum FilterDropdownVariant {
	Regular = 'regular',
	Slim = 'slim',
}

/**
 * Props for the CruiseSearchWidget component.
 *
 * @prop {string} filterKey - The key corresponding to the filter.
 * @prop {string} testId - The test id to identify the component in tests.
 * @prop {string} label - The label to display in the component.
 * @prop {number} amount - The amount of filter options selected for the filter.
 * @prop {string[]} selectedFilters - The currently selected list of filters for a given filter.
 * @prop {React.FC<SVGProps<SVGSVGElement>>} icon - The icon to display in the component.
 * @prop {Record<string, Filter>} filters - All the filters available to display in component.
 * @prop {boolean} isFullColor - Whether the component is displayed in full color.
 * @prop {boolean} isSelected - Whether the component is selected.
 * @prop {string} rootClass - A custom class to apply to the root container of the component.
 * @prop {string} badgeClass - A custom class to apply to the badge component.
 * @prop {boolean} useSelectionAsLabel - Wether to use the currently selected filters as the dropdown label.
 * @prop {FilterDropdownVariant} variant - The variant of the component to render.
 */
interface FilterDropdownProps {
	filterKey: string;
	testId: string;
	label: string;
	amount?: number;
	selectedOptions?: SelectedOption[];
	icon?: React.FC<SVGProps<SVGSVGElement>>;
	showCaret: boolean;
	isFullColor?: boolean;
	isSelected?: boolean;
	onClick?: () => void;
	rootClass?: string;
	badgeClass?: string;
	useSelectionAsLabel?: boolean;
	showBadge?: boolean;
	variant?: FilterDropdownVariant;
	innerRef?: React.MutableRefObject<HTMLButtonElement | null>;
}

const variantToClass: { [key in FilterDropdownVariant]: string } = {
	[FilterDropdownVariant.Regular]: styles.regular,
	[FilterDropdownVariant.Slim]: styles.slim,
};

export function FilterDropdown(props: Readonly<FilterDropdownProps>) {
	const { dictionary } = useAppContext();
	const { selectedOptions = [], label, amount, useSelectionAsLabel, variant = FilterDropdownVariant.Regular, innerRef, showBadge = true } = props;
	const handleClick = () => {
		if (props.onClick) {
			props.onClick();
		}
	};

	const contentLabel = useMemo(() => {
		if (useSelectionAsLabel && selectedOptions?.length > 0) {
			const option = selectedOptions[0]?.option;
			return mapLabel(option.formattedLabel ?? option.label, props.filterKey, dictionary);
		}
		return label;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [label, useSelectionAsLabel, selectedOptions]);

	const badgeAmount = useMemo(() => {
		if (useSelectionAsLabel) {
			if (amount && amount > 1) {
				return '+'.concat((amount - 1).toString());
			}

			return '';
		}

		return amount ? amount.toString() : '';
	}, [amount, useSelectionAsLabel]);

	return (
		<button
			ref={innerRef}
			className={clsx(styles.root, props.rootClass, variantToClass[variant], {
				[styles.isDimmed]: props.amount,
				[styles.isFullColor]: props.isFullColor,
				[styles.isSelected]: props.isSelected,
			})}
			data-testid={props.testId}
			onClick={handleClick}
		>
			<span className={styles.content}>
				{props.icon && <props.icon className={styles.icon} />}
				<span className={styles.label}>{contentLabel}</span>
				{variant === FilterDropdownVariant.Slim && amount && amount > 1 ? <span>+{amount - 1}</span> : <></>}
				{badgeAmount && showBadge && <span className={clsx(styles.badge, props.badgeClass)}>{badgeAmount}</span>}
			</span>
			{props.showCaret && <CaretIcon className={clsx(styles.icon, { [styles.caretUp]: props.isSelected })} />}
		</button>
	);
}
